<template>
  <label class="fts-toggle-switch" :class="getClass">
    <input
      :id="reference"
      type="checkbox"
      class="toggle-switch-checkbox"
      v-model="model"
      @change="$emit('change')"
    />

    <slot 
      name="label" 
      :label="getLabel" 
      :labelStyle="getLabelStyle" 
      :reference="reference"
      defaultClass="toggle-switch-label toggle-switch-label-left" 
      v-if="label && labelDir === 'left'">
      <span class="toggle-switch-label toggle-switch-label-left" 
        :class="labelClass"
        :style="getLabelStyle">
        {{getLabel}}
      </span>
    </slot>

    <div class="toggle-switch-handle" :style="getToggleSwitchHandleStyle" :class="{ 'toggle-switch-checked': model }">
      <div class="before-class" :style="[getBeforeClassStyle, model ? getActiveClass : '']"></div>
    </div>

    <slot 
      name="label" 
      :label="getLabel" 
      :labelStyle="getLabelStyle"
      :reference="reference"
      defaultClass="toggle-switch-label toggle-switch-label-right" 
      v-if="label && labelDir === 'right'">
      <span class="toggle-switch-label toggle-switch-label-right" :style="getLabelStyle">{{getLabel}}</span>
    </slot>
  </label>
</template>

<script>
import commons from "../commons/commons"

export default {
  props: {
    fullWidth: Boolean,
    value: Boolean,
    formData: Object,
    item: Object,
    size: {
      type: Number,
      default: 25
    },
    label: {
      type: Boolean,
      default: true
    },
    labelDir: {
      type: String,
      default: "right"
    },
    labelText: String,
    labelClass: String,
    matClass: Boolean,
    color: String,
  },
  model: {
    prop: "value",
    event: "updateValue",
  },
  computed: {
    theme() {
      const themes = ['primary', 'secondary', 'success', 'danger', 'warning', 'dark', 'ternary-green', 'ternary-blue', 'default'];
      if (this.color) 
        return themes.find(x => x === this.color.toString().toLowerCase()) || 'primary';

      return 'primary'
    },
    getClass() {
      let classes = `fts-toggle-switch-${this.theme}`;

      if (this.matClass)
        classes += ' fts-toggle-switch-mat';

      if (this.fullWidth)
        classes += ' toggle-switch--fw';

      return classes
    },
    model: {
      get() {
        if (this.item && this.item.fieldName)
          return this.formData[this.item.fieldName];
        else
          return this.value;
      },
      set(val) {
        if (this.item && this.item.fieldName)
          this.$set(this.formData, this.item.fieldName, val);
        else
          this.$emit('updateValue', val);
      },
    },
    getToggleSwitchHandleStyle() {
      return { 
        height: this.size + "px",
        width: this.size * 2 + "px",
        minWidth: this.size * 2 + "px",
      } 
    },
    getBeforeClassStyle() {
      return {
        height: this.matClass ? this.size + 2 + "px" :  this.size - 5 + "px",
        width: this.matClass ? this.size + 2 + "px" : this.size - 5 + "px"
      }
    },
    getActiveClass() {
      return  {
        left: this.size + 2 + "px",
      }
    },
    getLabel() {
      if (this.labelText)
        return this.labelText;
      else if (this.item && this.item.fieldName)
        return commons.getDisplayName(this.item.fieldName);
    },
    getLabelStyle() {
      return {
        fontSize: this.size - 6 + "px"
      }
    }
  },
  created() {
    this.reference = `fts-toggle-switch-${this._uid}`;
  }
}
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

$toggle-switch-colors: (
  'primary': fts-primary, 
  'secondary': fts-secondary,
  'success': fts-success,
  'danger': fts-danger,
  'warning': fts-warning,
  'dark': fts-dark,
  'ternary-green': fts-ternary-green,
  'ternary-blue': fts-ternary-blue,
  'default': fts-default
);
@each $name, $color in $toggle-switch-colors {
  .fts-toggle-switch {
    &.fts-toggle-switch-#{$name} {
      /* material design */
      &.fts-toggle-switch-mat {
        .toggle-switch-handle {
          &.toggle-switch-checked {
            @include theme() {
              background-color: lighten(theme-get($color), 35%);
            }
            .before-class {
              @include theme() {
                background-color: theme-get($color);
              }
            }
          }
        }
      }
      /* regular design */
      &:not(.fts-toggle-switch-mat) {
        .toggle-switch-handle {
          &.toggle-switch-checked {
            @include theme() {
              background-color: theme-get($color);
            }
          }
        }
      }
    }
  }
}
.fts-toggle-switch {
  @include user-select-none;
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  &.fts-toggle-switch-mat {
    .toggle-switch-handle {
      margin: 0 4px;
    }
    .before-class {
      left: -4px;
    }
    .toggle-switch-label {
      &.toggle-switch-label-left {
        margin-right: .675rem;
      } 
      &.toggle-switch-label-right {
        margin-left: .675rem;
      }
    }
  }
  &.toggle-switch--fw {
    display: flex;
    .toggle-switch__label {
      flex: 1;
    }
  }
  &.toggle-switch-label-l {
    .toggle-switch__label {
      margin-right: 0.375rem;
    }
  }
  &.toggle-switch-label-r {
    .toggle-switch__label {
      margin-left: 0.375rem;
    }
  }
}
.toggle-switch-handle {
  display: inline-block;
  background-color: var(--fts-gray-300);
  border-radius: 10em;
  position: relative;
  transition: background 0.3s;
  .before-class {
    @include absolute-center-y;
    left: 4px;
    transition: left 0.3s;
    display: block;
    border-radius: 50%;
    background-color: var(--fts-light);
    box-shadow: var(--fts-elevation-2);
    // box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 2px rgba(0,0,0,.12);
  }
}
.toggle-switch-checkbox {
  position: absolute;
  visibility: hidden;
}
.toggle-switch-label {
  position: relative;
  font-size: .75rem;
  font-weight: bold;
  &.toggle-switch-label-left {
    margin-right: .375rem;
  } 
  &.toggle-switch-label-right {
    margin-left: .375rem;
  }
}
/* override combine.css */
.fts-toggle-switch {
  max-width: unset;
  margin-bottom: 0;
  font-weight: normal;
}
</style>